import type { CanonicalMetadataCandidateScope } from '../../adapters/IDatabaseAdapter';
import { stableStringify } from '../checklist/ChecklistSnapshotCreationService';
import { HistoricalSnapshotHydrationService, historicalTables as t, loadHistoricalSnapshotRows, type HistoricalHydrationDependencies, type HistoricalRow } from './HistoricalSnapshotHydrationService';

const valuesTable = 'guincho_valores_checklist';
const missing = (value: unknown) => value == null || value === '';
export interface CanonicalMetadataCheckpoint { parent: string; value: string; parentDone: boolean; valueDone: boolean; completedAt?: number }
export interface CanonicalMetadataDependencies extends HistoricalHydrationDependencies {
  page(table: string, afterId: string, limit: number, scope: CanonicalMetadataCandidateScope): Promise<HistoricalRow[]>;
  patch(table: string, row: HistoricalRow, updates: HistoricalRow): Promise<boolean>;
  load(): CanonicalMetadataCheckpoint | null;
  save(state: CanonicalMetadataCheckpoint): void;
  now(): number;
}

/** Etapa interna do pull: consulta por UUID, sem reaplicar conteúdo ou mover datas. */
export const pullCanonicalMetadata = async (companyId: string, deps: CanonicalMetadataDependencies): Promise<{ changed: number; progressed: boolean; incomplete: boolean }> => {
  let state = deps.load() ?? { parent: '', value: '', parentDone: false, valueDone: false };
  if (state.completedAt != null) {
    if (deps.now() - state.completedAt < 60_000) return { changed: 0, progressed: false, incomplete: false };
    state = { parent: '', value: '', parentDone: false, valueDone: false };
  }
  const historicalEnabled = await deps.enabled();
  let changed = 0;
  let progressed = false;
  let requests = 0;
  const cache = new Map<string, Promise<HistoricalRow[]>>();
  const chains = new Map<string, ReturnType<typeof loadHistoricalSnapshotRows>>();
  const fetch: HistoricalHydrationDependencies['fetch'] = (table, filters, offset, limit) => {
    const key = JSON.stringify([table, filters, offset, limit]);
    if (!cache.has(key)) {
      if (++requests > 80) throw new Error('canonical_metadata_round_limit');
      cache.set(key, deps.fetch(table, filters, offset, limit));
    }
    return cache.get(key)!;
  };
  const remoteParent = async (parent: HistoricalRow) => {
    const rows = await fetch(t.parents, { id: parent.id, empresa_id: companyId }, 0, 2);
    const row = rows[0];
    return rows.length === 1 && row.id === parent.id && row.empresa_id === companyId
      && row.template_id === parent.template_id && row.usuario_id === parent.usuario_id ? row : null;
  };
  const protect = async (entries: Array<[string, HistoricalRow]>) => {
    await deps.refreshProtection?.();
    for (const [table, row] of entries) if (!await deps.allowed(table, row)) return false;
    return true;
  };
  for (const kind of ['parent', 'value'] as const) {
    const done = kind === 'parent' ? 'parentDone' : 'valueDone';
    if (kind === 'value' && !historicalEnabled) state[done] = true;
    if (state[done]) continue;
    const table = kind === 'parent' ? t.parents : valuesTable;
    const page = await deps.page(table, state[kind], 20, { companyId, historicalEnabled });
    for (const candidate of page) {
      // Limite encerra a rodada antes de examinar outro candidato; o último concluído permanece salvo.
      if (requests >= 80) return { changed, progressed, incomplete: true };
      const requestsBeforeCandidate = requests;
      let advanceCheckpoint = true;
      try {
        if (candidate.empresa_id !== companyId) continue;
        if (kind === 'parent') {
          if (candidate.retired_at != null) continue;
          if ((!historicalEnabled || candidate.snapshot_id != null) && !missing(candidate.sequencial_numero) && !missing(candidate.sequencial_codigo)) continue;
          if (!await protect([[t.parents, candidate]])) continue;
          if (historicalEnabled && candidate.snapshot_id == null) {
            changed += await new HistoricalSnapshotHydrationService({ ...deps, fetch, listParents: async () => [candidate] }).run(companyId);
          }
          if (!missing(candidate.sequencial_numero) && !missing(candidate.sequencial_codigo)) continue;
          const current = await deps.read(t.parents, candidate.id);
          if (!current || current.empresa_id !== companyId) continue;
          const remote = await remoteParent(current);
          if (!remote || remote.retired_at != null) continue;
          if ((!missing(remote.sequencial_numero) && (!Number.isSafeInteger(remote.sequencial_numero) || remote.sequencial_numero <= 0))
            || (!missing(remote.sequencial_codigo) && typeof remote.sequencial_codigo !== 'string')) continue;
          const patch: HistoricalRow = {};
          let contradictory = false;
          for (const column of ['sequencial_numero', 'sequencial_codigo']) {
            if (!missing(current[column]) && current[column] !== remote[column]) contradictory = true;
            if (missing(current[column]) && !missing(remote[column])) patch[column] = remote[column];
          }
          if (contradictory || !Object.keys(patch).length || !await protect([[t.parents, current]])) continue;
          if (await deps.patch(t.parents, current, patch)) changed++;
        } else {
          if (!missing(candidate.snapshot_field_id) || !candidate.campo_id || !await deps.enabled()) continue;
          const parent = await deps.read(t.parents, candidate.checklist_id);
          if (!parent?.snapshot_id || parent.empresa_id !== companyId || parent.retired_at != null) continue;
          if (!await protect([[t.parents, parent], [valuesTable, candidate]])) continue;
          const remote = await remoteParent(parent);
          if (!remote || remote.snapshot_id !== parent.snapshot_id || remote.retired_at != null) continue;
          const rows = await fetch(valuesTable, { id: candidate.id, checklist_id: parent.id, empresa_id: companyId }, 0, 2);
          const value = rows[0];
          if (rows.length !== 1 || value.id !== candidate.id || value.checklist_id !== parent.id || value.empresa_id !== companyId
            || (value.campo_id !== null && value.campo_id !== candidate.campo_id)
            || value.valor !== candidate.valor || !value.snapshot_field_id) continue;
          const chainKey = `${parent.id}:${parent.snapshot_id}`;
          if (!chains.has(chainKey)) chains.set(chainKey, loadHistoricalSnapshotRows({ fetch }, remote));
          const chain = await chains.get(chainKey)!;
          if (!chain) continue;
          const fields = chain.filter(([tableName, field]) => tableName === t.fields
            && (field.original_campo_id === candidate.campo_id || field.id === candidate.campo_id));
          if (fields.length !== 1 || fields[0][1].id !== value.snapshot_field_id) continue;
          // O vínculo só referencia estrutura canônica materializada, nunca um arquivo divergente.
          let valid = true;
          for (const [tableName, row] of chain) {
            const local = await deps.read(tableName, row.id);
            if (local && Object.keys(row).some(key => stableStringify(local[key] ?? null) !== stableStringify(row[key] ?? null))) { valid = false; break; }
          }
          if (!valid || !await protect([[t.parents, parent], ...chain, [valuesTable, candidate]])) continue;
          for (const [tableName, row] of chain) {
            if (!await deps.read(tableName, row.id)) await deps.insert(tableName, row);
            const stored = await deps.read(tableName, row.id);
            if (!stored || Object.keys(row).some(key => stableStringify(stored[key] ?? null) !== stableStringify(row[key] ?? null))) { valid = false; break; }
          }
          if (!valid || !await protect([[t.parents, parent], ...chain, [valuesTable, candidate]])) continue;
          if (await deps.patch(valuesTable, candidate, { snapshot_field_id: value.snapshot_field_id })) changed++;
        }
      } catch (error) {
        if (!(error instanceof Error) || error.message !== 'canonical_metadata_round_limit') throw error;
        // Retoma com orçamento novo se candidatos anteriores consumiram parte da rodada.
        if (requestsBeforeCandidate > 0) {
          advanceCheckpoint = false;
          return { changed, progressed, incomplete: true };
        }
        // Um candidato que sozinho excede o limite não pode impedir os seguintes.
      } finally {
        if (advanceCheckpoint) {
          state[kind] = candidate.id;
          progressed = true;
          deps.save({ ...state });
        }
      }
    }
    if (page.length < 20) state[done] = true;
    deps.save({ ...state });
  }
  const incomplete = !state.parentDone || !state.valueDone;
  if (!incomplete) { state.completedAt = deps.now(); deps.save({ ...state }); }
  return { changed, progressed, incomplete };
};
